<template>
  <div class="key-signature-zone">
    <div
      v-show="!editStatus"
      class="row"
    >
      <div class="key-signature-zone__tenant-signature hand-write-signature small-12 medium-12 large-8">
        <div class="columns key-signature-zone__preview-item small-6">
          <div class="input-field">
            <label>{{ $t('signature') }}</label>
            <SignatureField
              v-show="!editStatus"
              ref="tenantSignatureRef"
              :isEditable="!editStatus"
              class="signature-box pointer"
              :message="$t('hotb.signatures.tenantSignatory')"
              :hintBeforeSignature="$t('hint.signatureNotSave')"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="key-signature-zone__tenant medium-12 large-8">
        <div
          class="columns"
          :class="{
            'key-signature-zone__preview-item small-6': !editStatus,
            'small-4 large-6': editStatus
          }"
        >
          <InputField
            :readonly="!editStatus"
            :label="$t('hotb.signatures.name')"
            :showColon="false"
            :showFlag="false"
            :value="getSignatureAttribute('tenantName')"
            @updateValue="val=>updateValue(val,'tenantName')"
          />
        </div>
        <div
          class="columns"
          :class="{
            'key-signature-zone__preview-item': !editStatus,
            'small-4 large-6': editStatus
          }"
        >
          <InputField
            :readonly="!editStatus"
            :label="$t('hotb.signatures.contactTelNo')"
            :showColon="false"
            :showFlag="false"
            :value="getSignatureAttribute('tenantTeleNo')"
            @updateValue="val=>updateValue(val,'tenantTeleNo')"
          />
        </div>
      </div>
    </div>
    <div class="row key-signature-zone__for-row">
      <label class="label-title small-12">{{ $t('hotb.signatures.forHklandOnly') }}</label>
    </div>
    <div class="row">
      <div
        class="small-12"
        :class="{'key-signature-zone__preview-item-keys-handed-over': !editStatus}"
      >
        <input
          type="checkbox"
          :checked="true"
          :readonly="true"
          :disabled="true"
        >
        <span>{{ keyHotbCheckboxContent }}</span>
      </div>
      <div
        v-if="editStatus"
        class="row key-signature-zone__tenant-signature small-12"
      >
        <label class="small-4 columns">{{ $t('hotb.signatures.BMOStaffName') }}</label>
        <label class="small-4 columns">{{ $t('date') }}</label>
      </div>
      <div
        v-for="(item, index) in BmoNameAndDate"
        :key="index"
        class="hand-write-signature row"
        :class="{
          'small-8': !editStatus,
          'small-12': editStatus
        }"
      >
        <SignatureField
          v-show="!editStatus"
          ref="managerSignatureRefs"
          :isEditable="!editStatus"
          class="signature-box columns small-4 pointer"
          :message="$t('signature')"
          :hintBeforeSignature="$t('hint.signatureNotSave')"
        />
        <div
          :class="{
            'key-signature-zone__preview-item columns': !editStatus,
            'small-4 key-signature-zone__input-staff': editStatus
          }"
        >
          <template v-if="editStatus">
            <HKLInput
              :value="item.name"
              @change="val => updateBmoVal(val, index, 'name')"
            />
          </template>
          <template v-else>
            <div class="input-field">
              <span>{{ item.name | isEmptyValue }}</span>
            </div>
            <span
              v-if="!editStatus"
              class="signature-text"
            >{{ $t('hotb.signatures.BMOStaffName') }}</span>
          </template>
        </div>
        <div
          v-if="!editStatus"
          class="columns key-signature-zone__preview-item"
        >
          <div class="input-field">
            <span>{{ item.date | formatDate('DD MMM YYYY') | isEmptyValue }}</span>
          </div>
          <span class="signature-text">{{ $t('hotb.signatures.date') }}</span>
        </div>
        <div
          v-else
          :class="{
            'key-signature-zone__preview-item columns': !editStatus,
            'small-4 key-signature-zone__input-date': editStatus
          }"
        >
          <DatePicker
            class="small-12 key-signature-zone__date-picker"
            format="DD MMM YYYY"
            :calendarButton="true"
            calendarButtonIcon="fa fa-calendar"
            :value="toJsDate(item.date)"
            @selected="val => updateBmoVal(val, index, 'date')"
            @cleared="updateBmoVal('', index, 'date')"
          />
        </div>
        <div
          v-if="editStatus"
          class="key-signature__btn small-2 columns"
        >
          <span
            v-if="index === 0"
            class="underline pointer"
            @click="addRow"
          >
            {{ $t('action.add') }} +
          </span>
          <HKLDeleteIcon
            v-else
            @click="() => delRow(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, ref, unref } from 'vue-demi'
import DatePicker from '@/controls/DatePicker'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import HKLInput from '@/controls/Input/HKLInput.vue'
import InputField from '@/controls/InputField'
import SignatureField from '@/controls/SignatureField'
import { useStoreGetters, useStoreMutations } from '@/store/index'
import mutationTypes from '@/store/mutation-types'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'

export default {
  components: {
    HKLDeleteIcon,
    HKLInput,
    DatePicker,
    InputField,
    SignatureField,
  },
  props: {
    editStatus: Boolean,
  },
  setup (props) {
    const { updateHotbByPath } = useStoreMutations('hotb', {
      updateHotbByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
    })
    const { currentHotb, isHandover } = useStoreGetters('hotb', ['currentHotb', 'isHandover'])
    const tenantSignatureRef = ref(null)
    const managerSignatureRefs = ref(null)
    const emptyValue = computed(() => props.editStatus ? '' : i18n.t('emptyValue'))
    const fallbackNameAndDate = [{
      name: '',
      date: '',
    }]
    const defaultSignature = computed(() => {
      return {
        tenantName: '',
        tenantTeleNo: '',
        BmoNameAndDate: fallbackNameAndDate,
      }
    })
    const keySignatures = computed(() => {
      const currentKeySignatures = lodash.get(unref(currentHotb), 'keySignatures', {})
      const keySignatures = Object.assign({}, unref(defaultSignature), currentKeySignatures)
      return keySignatures
    })
    const keyHotbCheckboxContent = computed(() => {
      const localeKey = unref(isHandover) ? 'keysHandedOver' : 'keysTakenBack'
      return i18n.t(`hotb.signatures.${localeKey}`)
    })
    const BmoNameAndDate = computed({
      get () {
        const result = lodash.get(unref(keySignatures), 'BmoNameAndDate', fallbackNameAndDate) || fallbackNameAndDate
        return result.map(item => {
          return item
        })
      },
    })
    const getSignatureAttribute = (name) => {
      return lodash.get(unref(keySignatures), name) || unref(emptyValue)
    }
    const updateValue = (newValue, field) => {
      updateHotbByPath({
        path: ['keySignatures', field],
        newValue,
      })
    }
    const updateBmoVal = (val, index, key) => {
      const cloneBmoInfo = lodash.cloneDeep(unref(BmoNameAndDate))
      cloneBmoInfo[index][key] = val
      updateValue(cloneBmoInfo, 'BmoNameAndDate')
    }
    const addRow = () => {
      const cloneBmoInfo = lodash.cloneDeep(unref(BmoNameAndDate))
      cloneBmoInfo.push(fallbackNameAndDate[0])
      updateValue(cloneBmoInfo, 'BmoNameAndDate')
    }
    const delRow = (index) => {
      const cloneBmoInfo = lodash.cloneDeep(unref(BmoNameAndDate))
      cloneBmoInfo.splice(index, 1)
      updateValue(cloneBmoInfo, 'BmoNameAndDate')
    }
    const toJsDate = (dateString) => {
      return dateString ? new Date(dateString) : ''
    }
    const getImgUrl = () => {
      const managerSignatures = unref(managerSignatureRefs).map((item, index) => {
        return {
          img: item.imgUrl,
          name: unref(BmoNameAndDate)[index].name,
          date: dateUtil.formatByLongDate(unref(BmoNameAndDate)[index].date, 'LL'),
        }
      })
      return {
        tenantSignature: unref(tenantSignatureRef).imgUrl,
        managerSignatures: managerSignatures,
      }
    }
    return {
      keySignatures,
      keyHotbCheckboxContent,
      BmoNameAndDate,
      defaultSignature,
      tenantSignatureRef,
      managerSignatureRefs,
      getSignatureAttribute,
      updateValue,
      updateBmoVal,
      addRow,
      delRow,
      toJsDate,
      getImgUrl,
    }
  },
}
</script>

<style lang="scss" scoped>
.key-signature-zone {
  display: inline;
  &__tenant-signature {
    margin-top: 20px;
    .signature-box {
      flex: 1;
      ::v-deep .signature-link {
        position: relative;
        line-height: 18px;
      }
    }
  }
  &__tenant, &__tenant-signature {
    display: flex;
    .columns + .columns {
      margin-left: 40px;
    }
    .key-signature-zone__preview-item {
      $label-width: 140px;
      .input-field {
        text-align: left;
        ::v-deep label {
          margin-top: 15px;
          margin-bottom: 5px;
          font-weight: 700;
          + span {
            display: block;
            line-height: 1.43;
          }
        }
      }
    }
  }
  & ::v-deep .text-input {
    textarea,input{
      width: 100% !important;
    }
  }
  .label-title {
    margin-top: 15px;
    margin-bottom: 5px;
    font-size: 14px;
    line-height: 1.43;
    font-weight: bold;
    text-align: left;
    color: #0C3A58;
    &.columns {
      margin: 0
    }
  }
  .hand-write-signature {
    display: flex;
    align-items: flex-start;
    .columns + .columns {
      padding-left: 20px;
    }
  }
  &__date-picker {
    width: 100%;
  }
  .signature-text {
    width: 100%;
    font-size: 12px;
    margin-top: 0px;
    font-style: italic;
    text-align: center;
    display: block;
  }
  &__preview-item {
    .input-field {
      line-height: 40px;
      text-align: center;
    }
    .signature-text {
      border-top: 1px solid black
    }
    &-keys-handed-over {
      margin-bottom: 15px;
    }
  }
  &__input-staff {
    margin-bottom: 10px;
  }
  &__input-date {
    margin-left: 40px;
    margin-bottom: 10px;
  }
  &__for-row {
    margin-top: 15px;
  }
}
.key-signature {
  &__btn {
    align-self: flex-end;
    line-height: 40px;
    margin-bottom: 10px;
    margin-left: 40px;
  }
}
</style>
